import { Deck } from '../../../../../interfaces/src/lib/interfaces/index';
import { ShipSideEnum } from '../../../../../interfaces/src/lib/types/index';
import { createSlice } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';
import { mscGet, mscRemove } from '../../utilFuncs/mscStorageClient';

export interface DeckViewState {
  selectedDeckId: string;
  selectedCabinId: string;
  selectedCabinSide: string;
  selectedShipSection: ShipSideEnum;
  cabinElements: SVGElement[];
  availableDecks: Deck[] | undefined;
  availableCabins: number[] | undefined;
}

export type DeckViewType = {
  firstCabinSelections: DeckViewState;
  secondCabinSelections: DeckViewState;
  cabinNum: number;
};

const initialState: DeckViewType = {
  cabinNum: 1,
  firstCabinSelections: {
    selectedDeckId: '',
    selectedCabinId: '',
    selectedCabinSide: '',
    selectedShipSection: ShipSideEnum.BACK,
    cabinElements: [],
    availableDecks: undefined,
    availableCabins: [],
  },
  secondCabinSelections: {
    selectedDeckId: '',
    selectedCabinId: '',
    selectedCabinSide: '',
    selectedShipSection: ShipSideEnum.BACK,
    cabinElements: [],
    availableDecks: undefined,
    availableCabins: [],
  },
};

const loadDeckState = () => {
  const stored = mscGet<DeckViewType>('session', 'deckState');
  if (!stored) return initialState;
  mscRemove('session', 'deckState');
  return stored;
};

const deckViewSlice = createSlice({
  name: 'deckView',
  initialState: loadDeckState(),
  reducers: {
    setSelectedDeckId: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.selectedDeckId = action.payload.selectedDeckId;
      else if (action.payload.cabinNum === 2)
        state.secondCabinSelections.selectedDeckId = action.payload.selectedDeckId;
      else {
        state.firstCabinSelections.selectedDeckId = action.payload.selectedDeckId;
        state.secondCabinSelections.selectedDeckId = action.payload.selectedDeckId;
      }
    },
    setSelectedCabinId: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.selectedCabinId = action.payload.selectedCabinId;
      else if (action.payload.cabinNum === 2)
        state.secondCabinSelections.selectedCabinId = action.payload.selectedCabinId;
      else {
        state.firstCabinSelections.selectedCabinId = action.payload.selectedCabinId;
        state.secondCabinSelections.selectedCabinId = action.payload.selectedCabinId;
      }
    },
    setSelectedCabinSide: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.selectedCabinSide = action.payload.selectedCabinSide;
      else state.secondCabinSelections.selectedCabinSide = action.payload.selectedCabinSide;
    },
    setSelectedShipSection: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.selectedShipSection = action.payload.selectedShipSection;
      else state.secondCabinSelections.selectedShipSection = action.payload.selectedShipSection;
    },
    setCabinElements: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.cabinElements = action.payload.cabinElements;
      else state.secondCabinSelections.cabinElements = action.payload.cabinElements;
    },
    setAvailableDecks: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.availableDecks = action.payload.availableDecks;
      else state.secondCabinSelections.availableDecks = action.payload.availableDecks;
    },
    setAvailableCabins: (state, action) => {
      if (action.payload.cabinNum === 1)
        state.firstCabinSelections.availableCabins = action.payload.availableCabins;
      else state.secondCabinSelections.availableCabins = action.payload.availableCabins;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase('resetFromPriceType', () => {
        return initialState;
      })
      .addCase('resetFromOccupancy', () => {
        return initialState;
      })
      .addCase('resetFromCabinType', (state, action: any) => {
        const cabinNum = action.payload?.cabinNumber;
        if (cabinNum === 1) {
          state = initialState as WritableDraft<DeckViewType>;
        } else {
          state.secondCabinSelections =
            initialState.secondCabinSelections as WritableDraft<DeckViewState>;
        }
        return state;
      })
      .addCase('resetFromDeck', (state, action: any) => {
        const cabinNumber = action?.payload?.cabinNumber;
        if (cabinNumber === 1) {
          return {
            ...state,
            secondCabinSelections: initialState.secondCabinSelections,
          };
        }
        if (cabinNumber === 2) {
          return {
            ...state,
            firstCabinSelections: initialState.firstCabinSelections,
          };
        }
        return state;
      }),
});

export const {
  setSelectedDeckId,
  setSelectedCabinId,
  setSelectedCabinSide,
  setSelectedShipSection,
  setCabinElements,
  setAvailableDecks,
  setAvailableCabins,
} = deckViewSlice.actions;
export default deckViewSlice.reducer;
